import { CreditCard, ChevronRight, Download, Lock, PackageCheck, ShieldCheck, ShoppingCart, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fileUrl } from "@/lib/api";
import { stripRichText } from "@/components/RichText";

const PLACEHOLDER = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='400'%20height='400'%3E%3Crect%20width='400'%20height='400'%20fill='%23EEF2F7'/%3E%3Ctext%20x='200'%20y='210'%20font-family='Arial'%20font-size='24'%20font-weight='bold'%20fill='%2394A3B8'%20text-anchor='middle'%3EISMO-TECH%3C/text%3E%3C/svg%3E";

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

/** Aperçu du produit tel que le client le verra sur la page boutique. */
export default function ProductPreview({ open, onClose, form = {}, shopName = "" }) {
  const img = form.image_url ? fileUrl(form.image_url) : PLACEHOLDER;
  const price = Number(form.price || 0);
  const oldPrice = Number(form.old_price || 0);
  const discount = oldPrice > price && price > 0 ? Math.round((1 - price / oldPrice) * 100) : 0;
  const stock = form.stock_managed ? null : Number(form.manual_stock || 0);
  const inStock = stock === null ? true : stock > 0;
  const isDigital = !!form.product_type || form.delivery_mode !== "license_key";
  const hasDownload = !!form.product_type;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="vendor-product-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            👁 Aperçu client <span className="text-xs font-normal text-slate-400">— tel que vos clients le verront</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="relative overflow-hidden rounded-2xl border border-line bg-slate-50 p-5">
            {discount > 0 && (
              <span className="absolute left-3 top-3 rounded-full bg-rose-500 px-2.5 py-1 text-xs font-bold text-white">-{discount}%</span>
            )}
            <img src={img} alt={form.name || "Produit"} className="mx-auto max-h-72 w-full object-contain" />
          </div>

          <div>
            {form.license_type && (
              <p className="label-eyebrow text-brand">{form.license_type}</p>
            )}
            <h2 className="mt-1 font-display text-2xl font-extrabold text-ink">{form.name || "Nom du produit"}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {isDigital && <span className="rounded-full bg-amber-100 px-2.5 py-1 font-bold text-amber-700">Numérique</span>}
              <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-bold ${inStock ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-600"}`}>
                <PackageCheck className="h-3.5 w-3.5" /> {inStock ? (stock === null ? "En stock" : `En stock (${stock})`) : "Rupture"}
              </span>
              {shopName && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">{shopName}</span>}
            </div>

            {form.short_description && (
              <p className="mt-3 text-sm text-slate-600">{stripRichText(form.short_description)}</p>
            )}

            <div className="mt-4 rounded-2xl border border-line bg-white p-4">
              <div className="flex items-end gap-3">
                <span className="font-display text-3xl font-black text-ink">{fmt(price)}</span>
                {oldPrice > price && <span className="pb-1 text-sm text-slate-400 line-through">{fmt(oldPrice)}</span>}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <span className="btn-glow rounded-full bg-brand px-6 py-3 text-center text-sm font-bold text-white opacity-90">Acheter maintenant</span>
                <span className="flex items-center justify-center gap-2 rounded-full border-2 border-ink px-6 py-3 text-sm font-bold text-ink">
                  <ShoppingCart className="h-4 w-4" /> Ajouter au panier
                </span>
              </div>
            </div>

            {hasDownload && (
              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                <div className="flex items-center gap-2 bg-slate-900 px-4 py-2.5">
                  <Download className="h-4 w-4 shrink-0 text-white" />
                  <p className="text-xs font-extrabold uppercase tracking-wide text-white">📥 Téléchargement du produit</p>
                </div>
                <div className="bg-slate-50 p-4">
                  <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-slate-600">
                    <Lock className="h-4 w-4" /> 🔒 Téléchargement verrouillé
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">
                    Achetez ce produit et attendez la confirmation de votre paiement pour accéder au téléchargement.
                  </p>
                  <span className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-slate-300 px-6 py-3 text-sm font-bold text-slate-500">
                    <Lock className="h-4 w-4" /> Télécharger
                  </span>
                </div>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-1.5 text-[10.5px] font-bold text-slate-600">
              <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-sm"><CreditCard className="h-3 w-3 text-brand" /> 1. Paiement</span>
              <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
              <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-sm"><ShieldCheck className="h-3 w-3 text-amber-500" /> 2. Validation</span>
              <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
              <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-sm"><Zap className="h-3 w-3 text-emerald-600" /> 3. Livraison</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
